import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Shield, AlertTriangle, Plus, Loader2, Play, CheckCircle, XCircle, Eye, Trash2, RefreshCw, Flag } from 'lucide-react';
import { toast } from 'sonner';
import { getDetectionRules, createDetectionRule, deleteDetectionRule, updateDetectionRule, seedDetectionRules, runBreachDetectionScan, getDetectionAlerts, updateAlertStatus } from '@/services/dpdpa';

const severityColor = (s) => ({ critical: 'bg-red-600 text-white', high: 'bg-orange-500 text-white', medium: 'bg-amber-500 text-white', low: 'bg-blue-500 text-white' })[s] || 'bg-gray-500 text-white';
const statusColor = (s) => ({ open: 'bg-red-500/10 text-red-700', acknowledged: 'bg-blue-500/10 text-blue-700', resolved: 'bg-emerald-500/10 text-emerald-700', false_positive: 'bg-gray-500/10 text-gray-500' })[s] || '';

export default function BreachDetectionPage() {
  const [rules, setRules] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [form, setForm] = useState({ name: '', type: 'pii_exposure', severity: 'medium', description: '' });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [rulesData, alertsData] = await Promise.all([getDetectionRules(), getDetectionAlerts()]);
      setRules(rulesData.rules || []);
      setAlerts(alertsData.alerts || []);
    } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSeed = async () => {
    try { const r = await seedDetectionRules(); toast.success(r.message); load(); } catch { toast.error('Seed failed'); }
  };

  const handleRunScan = async () => {
    try {
      setScanning(true);
      const result = await runBreachDetectionScan();
      setScanResult(result);
      toast.success(`Scan complete: ${result.alerts_generated} alerts generated from ${result.rules_checked} rules`);
      load();
    } catch { toast.error('Scan failed'); } finally { setScanning(false); }
  };

  const handleCreate = async () => {
    if (!form.name) return toast.error('Name required');
    try { await createDetectionRule(form); toast.success('Rule created'); setShowCreate(false); setForm({ name: '', type: 'pii_exposure', severity: 'medium', description: '' }); load(); } catch { toast.error('Failed'); }
  };

  const handleAlertAction = async (alertId, status, notes = '') => {
    try { await updateAlertStatus(alertId, { status, notes }); toast.success(`Alert ${status}`); load(); } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin opacity-50" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="breach-detection-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Automated Breach Detection</h1>
          <p className="text-sm opacity-60">Real-time monitoring for PII exposure, consent anomalies, and access threats</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleRunScan} disabled={scanning} data-testid="run-scan-btn">
            {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            Run Detection Scan
          </Button>
          <Button onClick={() => setShowCreate(true)} variant="outline" data-testid="add-rule-btn"><Plus className="w-4 h-4 mr-2" /> Add Rule</Button>
          {rules.length === 0 && <Button onClick={handleSeed} variant="outline" data-testid="seed-rules-btn">Seed Defaults</Button>}
        </div>
      </div>

      {/* Scan Result Banner */}
      {scanResult && (
        <Card className={`border-l-4 ${scanResult.alerts_generated > 0 ? 'border-l-red-500 bg-red-500/5' : 'border-l-emerald-500 bg-emerald-500/5'}`} data-testid="scan-result-banner">
          <CardContent className="p-4 flex items-center gap-3">
            {scanResult.alerts_generated > 0 ? <AlertTriangle className="w-5 h-5 text-red-500" /> : <CheckCircle className="w-5 h-5 text-emerald-500" />}
            <div>
              <p className="font-medium">{scanResult.alerts_generated > 0 ? `${scanResult.alerts_generated} threat(s) detected` : 'No threats detected'}</p>
              <p className="text-xs opacity-60">{scanResult.rules_checked} rules checked</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alerts */}
      {alerts.filter(a => a.status === 'open').length > 0 && (
        <Card data-testid="open-alerts-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-500" /> Open Alerts ({alerts.filter(a => a.status === 'open').length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {alerts.filter(a => a.status === 'open').map(alert => (
              <div key={alert.id} className="flex items-center justify-between p-3 rounded-lg border bg-red-500/5" data-testid={`alert-${alert.id}`}>
                <div className="flex items-center gap-3">
                  <Badge className={severityColor(alert.severity)}>{alert.severity}</Badge>
                  <div>
                    <p className="text-sm font-medium">{alert.rule_name}</p>
                    <p className="text-xs opacity-60">{alert.rule_type} &middot; {new Date(alert.created_at).toLocaleString()}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => handleAlertAction(alert.id, 'acknowledged')} data-testid={`ack-${alert.id}`}>Acknowledge</Button>
                  <Button size="sm" variant="outline" onClick={() => handleAlertAction(alert.id, 'resolved')} data-testid={`resolve-${alert.id}`}><CheckCircle className="w-3 h-3 mr-1" /> Resolve</Button>
                  <Button size="sm" variant="ghost" className="text-gray-400" onClick={() => handleAlertAction(alert.id, 'false_positive', 'Marked as false positive')} data-testid={`fp-${alert.id}`}>
                    <Flag className="w-3 h-3 mr-1" /> False Positive
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Alert History */}
      {alerts.filter(a => a.status !== 'open').length > 0 && (
        <Card data-testid="alert-history-card">
          <CardHeader className="pb-3"><CardTitle className="text-base">Alert History</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {alerts.filter(a => a.status !== 'open').slice(0, 10).map(alert => (
              <div key={alert.id} className="flex items-center justify-between p-2 rounded border text-sm">
                <div className="flex items-center gap-2">
                  <Badge className={severityColor(alert.severity)} >{alert.severity}</Badge>
                  <span>{alert.rule_name}</span>
                </div>
                <Badge className={statusColor(alert.status)}>{alert.status.replace('_', ' ')}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Detection Rules */}
      <Card data-testid="detection-rules-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Shield className="w-4 h-4" /> Detection Rules ({rules.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {rules.map(rule => (
            <div key={rule.id} className="flex items-center justify-between p-3 rounded-lg border" data-testid={`rule-${rule.id}`}>
              <div className="flex items-center gap-3">
                <Badge className={severityColor(rule.severity)}>{rule.severity}</Badge>
                <div>
                  <p className="text-sm font-medium">{rule.name}</p>
                  <p className="text-xs opacity-50">{rule.type} &middot; Triggered: {rule.trigger_count || 0} &middot; FP: {rule.false_positive_count || 0}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={rule.enabled ? 'default' : 'secondary'}>{rule.enabled ? 'Active' : 'Disabled'}</Badge>
                <Button size="sm" variant="ghost" onClick={() => deleteDetectionRule(rule.id).then(load)}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          ))}
          {rules.length === 0 && <p className="text-sm text-center py-4 opacity-50">No detection rules configured. Click "Seed Defaults" to get started.</p>}
        </CardContent>
      </Card>

      {/* Create Rule Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Detection Rule</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="rule-name-input" /></div>
            <div><Label>Type</Label>
              <Select value={form.type} onValueChange={v => setForm(p => ({ ...p, type: v }))}>
                <SelectTrigger data-testid="rule-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pii_exposure">PII Exposure</SelectItem>
                  <SelectItem value="consent_anomaly">Consent Anomaly</SelectItem>
                  <SelectItem value="access_anomaly">Access Anomaly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Severity</Label>
              <Select value={form.severity} onValueChange={v => setForm(p => ({ ...p, severity: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button onClick={handleCreate} data-testid="create-rule-submit">Create Rule</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
